import React from 'react';
import { Box, Group, List, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconBug,
  IconCirclePlus,
  IconNotes,
  IconPencil,
  IconTool,
} from '@tabler/icons-react';

/** Métadonnées visuelles des rubriques release notes / changelog. */
const CHANGELOG_SECTION_META = {
  ajouté: { color: 'teal', Icon: IconCirclePlus },
  modifié: { color: 'blue', Icon: IconPencil },
  corrigé: { color: 'orange', Icon: IconBug },
  technique: { color: 'gray', Icon: IconTool },
  synthèse: { color: 'grape', Icon: IconNotes },
};

function renderSectionHeading(title, key, searchQuery) {
  const meta = CHANGELOG_SECTION_META[title.trim().toLowerCase()];
  if (!meta) {
    return (
      <Title key={key} order={5} mt="sm" mb={4}>
        {inlineMarkdown(title, searchQuery)}
      </Title>
    );
  }

  const { color, Icon } = meta;
  return (
    <Group key={key} gap="xs" mt="sm" mb={4} wrap="nowrap" align="center">
      <ThemeIcon size="md" radius="md" color={color} variant="light">
        <Icon size={16} stroke={1.75} aria-hidden />
      </ThemeIcon>
      <Title order={5} m={0}>
        {inlineMarkdown(title, searchQuery)}
      </Title>
    </Group>
  );
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightPlainText(text, searchQuery) {
  const query = searchQuery?.trim();
  if (!query) return text;

  const re = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const parts = String(text).split(re);

  return parts.map((part, i) => {
    if (part.toLowerCase() === query.toLowerCase()) {
      return (
        <Text
          key={i}
          component="span"
          style={{
            backgroundColor: 'var(--mantine-color-yellow-2)',
            borderRadius: 2,
            padding: '0 2px',
          }}
        >
          {part}
        </Text>
      );
    }
    return part;
  });
}

function inlineMarkdown(text, searchQuery) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Text key={i} component="span" fw={700}>
          {highlightPlainText(part.slice(2, -2), searchQuery)}
        </Text>
      );
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return (
        <Text key={i} component="span" fs="italic">
          {highlightPlainText(part.slice(1, -1), searchQuery)}
        </Text>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <Text
          key={i}
          component="span"
          ff="monospace"
          size="sm"
          px={4}
          style={{ background: 'var(--mantine-color-gray-1)', borderRadius: 4 }}
        >
          {highlightPlainText(part.slice(1, -1), searchQuery)}
        </Text>
      );
    }
    return highlightPlainText(part, searchQuery);
  });
}

function lineMatchesSearch(line, searchQuery) {
  const query = searchQuery?.trim().toLowerCase();
  if (!query) return true;
  return line.trim().toLowerCase().includes(query);
}

function markdownHeadingLevel(line) {
  const match = line.trim().match(/^(#{1,6})\s+/);
  return match ? match[1].length : 0;
}

function keepHeadingSection(lines, keep, startIndex) {
  const level = markdownHeadingLevel(lines[startIndex]);
  if (!level) return;

  for (let j = startIndex + 1; j < lines.length; j += 1) {
    const nextLevel = markdownHeadingLevel(lines[j]);
    if (nextLevel > 0 && nextLevel <= level) break;
    keep[j] = true;
  }
}

function filterMarkdownLines(lines, searchQuery) {
  const query = searchQuery?.trim();
  if (!query) return lines;

  const keep = new Array(lines.length).fill(false);
  for (let i = 0; i < lines.length; i += 1) {
    if (!lineMatchesSearch(lines[i], query)) continue;
    keep[i] = true;
    if (markdownHeadingLevel(lines[i]) > 0) {
      keepHeadingSection(lines, keep, i);
      continue;
    }
    for (let j = i; j >= 0; j -= 1) {
      const trimmed = lines[j].trim();
      const level = markdownHeadingLevel(lines[j]);
      if (level > 0) {
        keep[j] = true;
        keepHeadingSection(lines, keep, j);
        break;
      }
      if (!trimmed) break;
    }
  }

  const filtered = [];
  let pendingBlank = false;
  for (let i = 0; i < lines.length; i += 1) {
    if (!keep[i]) continue;
    const line = lines[i];
    if (!line.trim()) {
      pendingBlank = filtered.length > 0;
      continue;
    }
    if (pendingBlank) {
      filtered.push('');
      pendingBlank = false;
    }
    filtered.push(line);
  }
  return filtered;
}

/**
 * Rendu Markdown minimal (titres, listes, gras/italique) pour les notices d'aide.
 * @param {string} markdown
 * @param {{ searchQuery?: string }} [options]
 */
export function renderSimpleMarkdown(markdown, options = {}) {
  const { searchQuery = '' } = options;
  const rawLines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const lines = filterMarkdownLines(rawLines, searchQuery);
  const blocks = [];
  let listItems = [];
  let fenceLines = [];
  let inFence = false;

  const flushFence = () => {
    if (!fenceLines.length) return;
    blocks.push(
      <Box
        key={`fence-${blocks.length}`}
        component="pre"
        m={0}
        p="sm"
        ff="monospace"
        fz="sm"
        lh={1.5}
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: 'var(--mantine-color-gray-1)',
          borderRadius: 'var(--mantine-radius-sm)',
          overflowX: 'auto',
        }}
      >
        {highlightPlainText(fenceLines.join('\n'), searchQuery)}
      </Box>,
    );
    fenceLines = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <List key={`list-${blocks.length}`} size="sm" spacing="xs" withPadding>
        {listItems.map((item, i) => (
          <List.Item key={i}>{inlineMarkdown(item, searchQuery)}</List.Item>
        ))}
      </List>,
    );
    listItems = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      if (inFence) {
        flushFence();
        inFence = false;
      } else {
        flushList();
        inFence = true;
      }
      continue;
    }

    if (inFence) {
      fenceLines.push(line);
      continue;
    }

    if (!trimmed) {
      flushList();
      continue;
    }

    if (trimmed.startsWith('- ')) {
      listItems.push(trimmed.slice(2));
      continue;
    }

    flushList();

    if (trimmed.startsWith('### ')) {
      blocks.push(renderSectionHeading(trimmed.slice(4), blocks.length, searchQuery));
    } else if (trimmed.startsWith('## ')) {
      blocks.push(
        <Title key={blocks.length} order={4} mt="md" mb={6}>
          {inlineMarkdown(trimmed.slice(3), searchQuery)}
        </Title>,
      );
    } else if (trimmed.startsWith('# ')) {
      blocks.push(
        <Title key={blocks.length} order={3} mb="xs">
          {inlineMarkdown(trimmed.slice(2), searchQuery)}
        </Title>,
      );
    } else {
      blocks.push(
        <Text key={blocks.length} size="sm" lh={1.55}>
          {inlineMarkdown(trimmed, searchQuery)}
        </Text>,
      );
    }
  }

  flushList();
  flushFence();
  return blocks;
}

/** Nombre de lignes affichées après filtrage (pour l'indicateur de recherche). */
export function countMarkdownSearchMatches(markdown, searchQuery) {
  const query = searchQuery?.trim();
  if (!query) return 0;
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  return filterMarkdownLines(lines, searchQuery).filter((line) => line.trim()).length;
}
